import { Link } from 'react-router-dom';
import { Heart, MessageCircle, Building2, Newspaper, Calendar, MoreHorizontal } from 'lucide-react';
import ShareMenu from './ShareMenu';

const SHARE_TYPE_META = {
  property: { icon: Building2, label: 'Propiedad', color: 'from-cyan-500 to-blue-600' },
  news: { icon: Newspaper, label: 'Noticia', color: 'from-blue-500 to-indigo-600' },
  event: { icon: Calendar, label: 'Evento', color: 'from-amber-500 to-orange-600' },
};

function timeAgo(iso) {
  const diff = Date.now() - new Date(iso).getTime();
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return 'ahora';
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} h`;
  return `${Math.floor(hours / 24)} d`;
}

export default function PostCard({ post, onLike, href, showComments = true, compact = false }) {
  const Chip = post.share_type && SHARE_TYPE_META[post.share_type];
  const linkTo = href || `/app/feed/${post.post_id}`;
  const shareLink = post.share_ref?.url;

  return (
    <article className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm" data-testid={`post-card-${post.post_id}`}>
      {/* Header */}
      <div className="flex items-start gap-3 p-4">
        {post.author_picture ? (
          <img src={post.author_picture} alt="" className="h-11 w-11 shrink-0 rounded-full object-cover ring-2 ring-white" />
        ) : (
          <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-cnib-blue font-heading text-sm font-bold text-white ring-2 ring-white">
            {post.author_name?.charAt(0) || 'S'}
          </span>
        )}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <p className="truncate text-sm font-extrabold text-slate-900">{post.author_name}</p>
            {post.author_role === 'member' && (
              <span className="rounded-full border border-emerald-500/40 bg-emerald-50 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-emerald-700">
                Socio
              </span>
            )}
            {(post.author_role === 'admin' || post.author_role === 'superadmin') && (
              <span className="rounded-full border border-orange-500/40 bg-orange-50 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-orange-600">
                CNIB
              </span>
            )}
          </div>
          <p className="text-[11px] text-slate-400">
            {post.author_member_number ? `${post.author_member_number} · ` : ''}
            hace {timeAgo(post.created_at)}
          </p>
        </div>
        {Chip && (
          <span className={`shrink-0 rounded-full bg-gradient-to-r ${Chip.color} px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-white`}>
            {Chip.label}
          </span>
        )}
      </div>

      {/* Text */}
      {post.text && (
        <Link to={linkTo} className="block px-4 pb-3">
          <p className={`whitespace-pre-line text-sm leading-relaxed text-slate-800 ${compact ? 'line-clamp-4' : ''}`}>{post.text}</p>
        </Link>
      )}

      {/* Images gallery */}
      {Array.isArray(post.images) && post.images.length > 0 && (
        <Link to={linkTo} className="block">
          {post.images.length === 1 ? (
            <img src={post.images[0]} alt="" className="w-full max-h-[520px] object-cover" />
          ) : (
            <div className={`grid gap-1 ${post.images.length === 2 ? 'grid-cols-2' : 'grid-cols-2'}`}>
              {post.images.slice(0, 4).map((url, i) => (
                <img key={url + i} src={url} alt="" className="aspect-square w-full object-cover" />
              ))}
            </div>
          )}
        </Link>
      )}

      {/* Shared ref card */}
      {post.share_ref && (
        <Link
          to={post.share_ref.url || '#'}
          className="mx-4 mb-3 mt-1 flex items-stretch overflow-hidden rounded-xl border border-slate-200 bg-slate-50 transition-colors hover:bg-slate-100"
          data-testid={`post-card-share-${post.post_id}`}
        >
          {post.share_ref.image && (
            <img src={post.share_ref.image} alt="" className="h-24 w-28 shrink-0 object-cover" />
          )}
          <div className="flex min-w-0 flex-1 flex-col justify-center p-3">
            <p className="text-[10px] font-bold uppercase tracking-wider text-orange-500">
              {Chip?.label}
            </p>
            <p className="line-clamp-2 text-sm font-bold text-slate-900">{post.share_ref.title}</p>
            <p className="truncate text-xs text-slate-500">{post.share_ref.subtitle}</p>
          </div>
        </Link>
      )}

      {/* Stats row */}
      {(post.like_count > 0 || post.comment_count > 0) && (
        <div className="flex items-center justify-between px-4 pb-2 text-xs text-slate-500">
          <span className="flex items-center gap-1">
            {post.like_count > 0 && (
              <>
                <span className="flex h-4 w-4 items-center justify-center rounded-full bg-orange-500 text-white">
                  <Heart className="h-2.5 w-2.5 fill-white" />
                </span>
                {post.like_count}
              </>
            )}
          </span>
          <span>{post.comment_count > 0 ? `${post.comment_count} comentarios` : ''}</span>
        </div>
      )}

      {/* Action bar */}
      <div className="flex items-center justify-around border-t border-slate-100 px-2 py-1">
        <button
          type="button"
          onClick={() => onLike?.(post.post_id)}
          data-testid={`post-card-like-${post.post_id}`}
          className={`flex flex-1 items-center justify-center gap-1.5 rounded-lg px-3 py-2.5 text-sm font-bold transition-colors ${
            post.liked_by_me ? 'text-orange-500' : 'text-slate-500 hover:bg-slate-50'
          }`}
        >
          <Heart className={`h-4 w-4 ${post.liked_by_me ? 'fill-orange-500' : ''}`} />
          Me gusta
        </button>
        {showComments && (
          <Link
            to={linkTo}
            data-testid={`post-card-comment-${post.post_id}`}
            className="flex flex-1 items-center justify-center gap-1.5 rounded-lg px-3 py-2.5 text-sm font-bold text-slate-500 transition-colors hover:bg-slate-50"
          >
            <MessageCircle className="h-4 w-4" />
            Comentar
          </Link>
        )}
        <div className="flex-1 flex justify-center">
          <ShareMenu
            title={post.share_ref?.title || post.text?.slice(0, 80) || 'Publicación CNIB'}
            url={shareLink || `/app/feed/${post.post_id}`}
            compact
            testid={`post-card-share-${post.post_id}`}
          />
        </div>
      </div>
    </article>
  );
}
